import {
    type AppointmentReminder,
    AppointmentTypeFilterOption,
    CancellationOption,
    Deadline,
    RebookingOption,
} from "@accurx/api/appointment";
import { FeatureName, useFeatureFlag } from "@accurx/auth";
import { Ds } from "@accurx/design";
import {
    type TemplateType,
    useAvailableFilters,
    useMessageParts,
} from "domains/appointment-reminders";
import styled from "styled-components";

/** TODO: can be removed when we update AppointmentReminder cancellation/rebooking/changeDeadline DTOs to be non-nullable */
function notEmpty<T>(value: T | null | undefined): value is NonNullable<T> {
    return value !== undefined && value !== null;
}

const StyledList = styled.ul`
    margin: 0;
    padding-left: 1.5rem;
`;

const cancellationSupportMessageMap = {
    [CancellationOption.Automatic]:
        "Patient can cancel and their appointment will be removed automatically",
    [CancellationOption.Manual]: "Patient can request to cancel",
};

const changeDeadlineMessageMap = {
    [Deadline.OneHour]: "1 hour before appointment",
    [Deadline.OneWorkingDay]: "1 working day before appointment",
    [Deadline.ThreeWorkingDays]: "3 working days before appointment",
};

export type ViewReminderDetailsModalProps = {
    isOpen: boolean;
    onOpenChange: () => void;
    reminder: AppointmentReminder;
    appointmentTypeOptions: AppointmentTypeFilterOption[];
    workspaceHasMultipleSites: boolean;
    workspaceName?: string;
};

export const ViewReminderDetailsModal = ({
    isOpen,
    onOpenChange,
    reminder,
    appointmentTypeOptions,
    workspaceHasMultipleSites,
    workspaceName,
}: ViewReminderDetailsModalProps) => {
    const availableFilters = useAvailableFilters();
    const isAppointmentChangesEnabled = useFeatureFlag(
        FeatureName.AppointmentRemindersConfigAmendmentsUIv1,
    );

    const { fullMessage, fullPostMessage } = useMessageParts({
        templateType: reminder.type as TemplateType,
        customMessage: reminder.customMessageBody || "",
        postAppointmentMessageBody: reminder.postAppointmentCustomMessage || "",
        workspaceHasMultipleSites,
        workspaceName: workspaceName || "",
    });

    const labels = {
        siteLocationsOrClinics:
            availableFilters === "site-name-slot-type"
                ? "Site locations"
                : "Clinics",
        slotTypesOrAppointmentType:
            availableFilters === "site-name-slot-type"
                ? "Slot types"
                : "Appointment type",
    };

    const appointmentTypesAsDescription = reminder.appointmentTypeFilters.map(
        (atf) =>
            appointmentTypeOptions.find((ato) => ato.value === atf)
                ?.description ?? atf,
    );

    return (
        <Ds.Modal open={isOpen} onOpenChange={onOpenChange}>
            <Ds.Modal.Section>
                <Ds.Flex flexDirection="column" gap="2">
                    <Ds.Flex alignItems="center" justifyContent="space-between">
                        <Ds.Modal.Title>Reminder preview</Ds.Modal.Title>
                        <Ds.Modal.CloseButton />
                    </Ds.Flex>
                    <Ds.Box>
                        <Ds.Text mb="0.5" weight="bold">
                            {labels.siteLocationsOrClinics}
                        </Ds.Text>
                        <Ds.Flex gap="0.5" flexWrap="wrap">
                            {availableFilters === "site-name-slot-type"
                                ? reminder.siteNames.map((site) => (
                                      <Ds.Badge key={site} color="greyscale">
                                          {site}
                                      </Ds.Badge>
                                  ))
                                : reminder.slotTypes.map((slot) => (
                                      <Ds.Badge key={slot} color="greyscale">
                                          {slot}
                                      </Ds.Badge>
                                  ))}
                        </Ds.Flex>
                    </Ds.Box>
                    <Ds.Box>
                        <Ds.Text mb="0.5" weight="bold">
                            {labels.slotTypesOrAppointmentType}
                        </Ds.Text>
                        <Ds.Flex gap="0.5" flexWrap="wrap">
                            {availableFilters === "site-name-slot-type"
                                ? reminder.slotTypes.map((st) => (
                                      <Ds.Badge key={st} color="greyscale">
                                          {st}
                                      </Ds.Badge>
                                  ))
                                : appointmentTypesAsDescription.map((at) => (
                                      <Ds.Badge key={at} color="greyscale">
                                          {at}
                                      </Ds.Badge>
                                  ))}
                        </Ds.Flex>
                    </Ds.Box>
                    <Ds.Box>
                        <Ds.Text mb="0.5" weight="bold">
                            Reminder message
                        </Ds.Text>
                        <Ds.Text>{fullMessage}</Ds.Text>
                    </Ds.Box>
                    <Ds.Box>
                        <Ds.Text mb="0.5" weight="bold">
                            Scheduling
                        </Ds.Text>
                        <Ds.Text>Send before appointment:</Ds.Text>
                        <StyledList>
                            <Ds.Text as="li">1 working day</Ds.Text>
                            {reminder.threeWorkingDayReminderEnabled && (
                                <Ds.Text as="li">3 working days</Ds.Text>
                            )}
                            {reminder.oneWeekReminderEnabled && (
                                <Ds.Text as="li">1 week</Ds.Text>
                            )}
                        </StyledList>
                    </Ds.Box>

                    {isAppointmentChangesEnabled &&
                        notEmpty(reminder.appointmentChangingDeadline) &&
                        notEmpty(reminder.appointmentCancellationSupport) &&
                        notEmpty(reminder.appointmentRebookingSupport) && (
                            <Ds.Box>
                                <Ds.Text mb="0.5" weight="bold">
                                    Appointment changes
                                </Ds.Text>
                                <StyledList>
                                    <Ds.Text as="li">
                                        {
                                            cancellationSupportMessageMap[
                                                reminder
                                                    .appointmentCancellationSupport
                                            ]
                                        }
                                    </Ds.Text>
                                    {reminder.appointmentRebookingSupport ===
                                        RebookingOption.Manual && (
                                        <Ds.Text as="li">
                                            Patient can request to rebook
                                        </Ds.Text>
                                    )}
                                    <Ds.Text as="li">
                                        Deadline for changes:{" "}
                                        {
                                            changeDeadlineMessageMap[
                                                reminder
                                                    .appointmentChangingDeadline
                                            ]
                                        }
                                    </Ds.Text>
                                </StyledList>
                            </Ds.Box>
                        )}

                    <Ds.Box>
                        <Ds.Text mb="0.5" weight="bold">
                            Post-appointment message
                        </Ds.Text>
                        <Ds.Text data-testid="post-appointment-message">
                            {reminder.postAppointmentMessageEnabled
                                ? fullPostMessage
                                : "Off"}
                        </Ds.Text>
                    </Ds.Box>
                </Ds.Flex>
            </Ds.Modal.Section>
        </Ds.Modal>
    );
};
